// 채팅 화면 레이아웃·스크롤·로컬 UI 상태(독립). 데이터 의존 없음.
import { useCallback, useEffect, useRef, useState } from 'react';
import useIsMobile from '../../../hooks/useIsMobile';
import useVisualViewport from '../../../hooks/useVisualViewport';

export default function useChatUI() {
  const mobile = useIsMobile();           // 모바일(≤768px): 대화목록 off-canvas 드로어 + 하단 탭바 여백
  const { viewportHeight, keyboardOpen, keyboardHeight } = useVisualViewport();
  const [listWidth, setListWidth]         = useState(() => { const s = parseInt(localStorage.getItem('chatListWidth')); return (s >= 180 && s <= 460) ? s : 280; });
  const [convDrawerOpen, setConvDrawerOpen] = useState(false);   // 모바일 대화목록 드로어 열림
  const [showArtifacts, setShowArtifacts] = useState(false);
  const [saveMenu, setSaveMenu]           = useState(null);   // 저장 메뉴 열린 메시지 인덱스
  const [projectsOpen, setProjectsOpen]   = useState(true);
  const [showScrollDown, setShowScrollDown] = useState(false);  // 스크롤 위로 올렸을 때 '맨 아래로' 버튼 표시
  const bottomRef   = useRef(null);
  const scrollRef   = useRef(null);   // 메시지 스크롤 컨테이너
  const atBottomRef = useRef(true);   // 사용자가 맨 아래 근처에 있는지(자동 추적용, 동기 참조)
  const previousKeyboardOpenRef = useRef(keyboardOpen);
  const previousViewportHeightRef = useRef(viewportHeight);

  // 스크롤 위치 감지 → 맨 아래 근처 여부 갱신 + '맨 아래로' 버튼 표시
  const handleMessagesScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    atBottomRef.current = nearBottom;
    setShowScrollDown(!nearBottom);
  }, []);

  const scrollToBottom = useCallback(() => {
    atBottomRef.current = true;
    setShowScrollDown(false);
    // ChatInput의 모바일 포커스 경로에서도 호출되어 키보드 resize 뒤 최신 메시지를 유지한다.
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const wasOpen = previousKeyboardOpenRef.current;
    previousKeyboardOpenRef.current = keyboardOpen;
    if (!wasOpen && keyboardOpen) scrollToBottom();
  }, [keyboardOpen, scrollToBottom]);

  useEffect(() => {
    const previousHeight = previousViewportHeightRef.current;
    previousViewportHeightRef.current = viewportHeight;
    if (previousHeight === viewportHeight || !atBottomRef.current || !scrollRef.current) return undefined;

    const frame = window.requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (el && atBottomRef.current) el.scrollTop = el.scrollHeight;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [viewportHeight]);

  return {
    mobile, listWidth, setListWidth, convDrawerOpen, setConvDrawerOpen, showArtifacts, setShowArtifacts,
    saveMenu, setSaveMenu, projectsOpen, setProjectsOpen, showScrollDown, setShowScrollDown,
    bottomRef, scrollRef, atBottomRef, handleMessagesScroll, scrollToBottom,
    viewportHeight, keyboardOpen, keyboardHeight,
  };
}
